import { useQuery } from '@tanstack/react-query';
import { getToken } from '../../utils/auth';
import { useState, useEffect } from 'react';

import { ArrowPathIcon } from '@heroicons/react/24/outline';

import useQueryParams from '../../hooks/useQueryParams';
import CheckListWidget from '../common/CheckListWidget';

const { VITE_API_SERVICE } = import.meta.env;

export default function ReportCountries() {
  const { setQueryParam, removeQueryParam, getParam } = useQueryParams();
  const [selectedCountries, setSelectedCountries] = useState(() => {
    const countries = getParam('countries');
    return countries ? new Set(countries.split(',')) : new Set();
  });

  /**
   * Handle country change
   * @param {string} countryId
   * @param {boolean} checked
   */
  function handleCountryChange(countryId, checked) {
    const newCountries = new Set(selectedCountries);
    if (checked) {
      newCountries.add(countryId);
    } else {
      newCountries.delete(countryId);
    }

    setSelectedCountries(newCountries);

    if (newCountries.size > 0) {
      setQueryParam('countries', Array.from(newCountries).join(','));
    } else {
      removeQueryParam('countries');
    }
  }

  /**
   * Clear all metrics
   */
  function clearAllCountries() {
    setSelectedCountries(new Set());
    removeQueryParam('countries');
  }

  /**
   * Select all countries
   */
  function selectAllCountries() {
    if (!data?.countries) return;
    const allCountryIds = new Set(data.countries.map((c) => c.id));
    setSelectedCountries(allCountryIds);
    setQueryParam('countries', Array.from(allCountryIds).join(','));
  }

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['countries'],
    queryFn: async function () {
      const token = getToken();

      const response = await fetch(`${VITE_API_SERVICE}/v1/reports/countries`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error(data.message);
        }

        throw new Error('Failed to fetch countries');
      }

      return data;
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: false
  });

  // Watch for URL parameter changes
  useEffect(() => {
    const countries = getParam('countries');
    if (!countries) {
      setSelectedCountries(new Set());
    }
  }, [getParam('countries')]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-40">
        <span className="loading loading-spinner loading-md"></span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="collapse collapse-arrow shadow-sm">
        <input type="checkbox" defaultChecked />
        <div className="collapse-title font-medium">
          <h4>Countries</h4>
        </div>
        <div className="collapse-content">
          <div className="space-y-4">
            <div className="text-center">
              <p className="text-sm text-muted-foreground mb-2">
                {error.message}
              </p>
              <button
                onClick={() => refetch()}
                className="btn btn-sm btn-ghost gap-2"
                aria-label="Retry loading metrics"
              >
                <ArrowPathIcon className="h-4 w-4" />
                <span className="text-sm">Retry</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <CheckListWidget
      title="Countries"
      searchPlaceholder="Search countries..."
      items={data.countries}
      selectedItems={selectedCountries}
      onItemChange={handleCountryChange}
      onClearAll={clearAllCountries}
      onSelectAll={selectAllCountries}
    />
  );
}
